import { Title, useTranslate } from 'react-admin';
import { dataProvider } from '@src/dataProvider';
import { FileUpload } from '@components/FileUpload';
import { dataObject } from '@src/object.type';
import { entityResource as ent } from '@src/entityResource';
import { toDec } from '@src/helpers/numeric.ext';
import { sanitizeUpper } from '@helpers/string.ext';

const headers = ['kode_barang', 'jumlah_minimal', 'harga_satuan'];

export const PriceUpload = () => {
    const translate = useTranslate();
    const verifyData = async (data: dataObject[]) => {
        const normalizedData = data
            .filter((row: dataObject) => {
                // Check if at least one field has a value
                return (
                    row.kode_barang || row.jumlah_minimal || row.harga_satuan
                );
            })
            .map((row: dataObject) => {
                return {
                    ...row,
                    kode_barang: String(row.kode_barang)
                        .replace(/\s/g, '')
                        .replace(/"/g, ''),
                };
            });

        // distinct kode_barang
        const pricesCode = normalizedData.map((d: dataObject) => d.kode_barang);
        const uniquePricesCode = [...new Set(pricesCode)];

        // get goods from API
        const existingGoods = await dataProvider.getMany(ent.GOODS.NAME, {
            ids: uniquePricesCode,
            meta: { column: 'code', page: 1, perPage: pricesCode.length + 1 },
        });

        // map existing goods id to normalizedData
        const normalizedDataWithGoodsId = normalizedData.map(
            (d: dataObject) => {
                const { kode_barang, jumlah_minimal, harga_satuan } = d;
                const goodsId = existingGoods.data.find(
                    (data: dataObject) => data.code === kode_barang
                )?.id;

                return {
                    ...(goodsId && { goodsId: goodsId }),
                    ...(kode_barang && { kode_barang: kode_barang }),
                    ...(jumlah_minimal && { jumlah_minimal: jumlah_minimal }),
                    ...(harga_satuan && {
                        harga_satuan: toDec(String(harga_satuan)),
                    }),
                };
            }
        );

        const goodsIds = existingGoods.data.map((d: dataObject) => d.id);
        const existingPrices = await dataProvider.getMany(ent.PRICE.NAME, {
            ids: goodsIds,
            meta: {
                column: 'goodsId',
                page: 1,
                perPage: normalizedDataWithGoodsId.length + 1,
            },
        });

        // Filter out normalizedData items that have matching data_kode_barang in existingGoods.data
        const filteredData = normalizedDataWithGoodsId.filter(
            (src: dataObject) => {
                return !existingPrices.data.some((price: dataObject) => {
                    return (
                        sanitizeUpper(String(src.goodsId)) ===
                            sanitizeUpper(String(price.goodsId)) &&
                        toDec(String(src.jumlah_minimal)) ===
                            toDec(String(price.minimumOrder)) &&
                        toDec(String(src.harga_satuan)) ===
                            toDec(String(price.price))
                    );
                });
            }
        );

        return filteredData.map((d: dataObject) => {
            const { goodsId, jumlah_minimal, harga_satuan } = d;
            const existingId = existingPrices.data.find(
                (price: dataObject) =>
                    sanitizeUpper(String(goodsId)) ===
                        sanitizeUpper(String(price.goodsId)) &&
                    toDec(String(jumlah_minimal)) ===
                        toDec(String(price.minimumOrder))
            )?.id;
            return {
                ...(existingId && { id: existingId }),
                ...(goodsId && { goodsId: goodsId }),
                ...(jumlah_minimal && { minimumOrder: jumlah_minimal }),
                ...(harga_satuan && { price: harga_satuan }),
            };
        });
    };

    return (
        <>
            <Title title={translate('resources.price.action.upload')} />
            <FileUpload
                headers={headers}
                fileInputLabel={translate('resources.price.action.upload')}
                api={ent.PRICE.UPLOAD_API}
                resource={ent.PRICE.NAME}
                verifyData={verifyData}
            />
        </>
    );
};
